import React from "react";
import classNames from "classnames";
import type { RenderElementProps } from "slate-react";
import type {
    EntityRef,
    EntityRefKind,
} from "@common/schemas/chatEntityRef.schema";
import {
    AccountPickerIcon,
    getAccountPickerDescription,
} from "@components/financialAccount/AccountPickerItem";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { LedgerIcon, TransactionIcon } from "@icons";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { StateAwareNavLink } from "@components/routes/StateAwareNavLink";
import { resolveEntityRef, type EntityRefResolution } from "./resolveEntityRef";
import { CounterpartyGlyph } from "./CounterpartyGlyph";
import { TaskGlyph } from "./TaskGlyph";
import {
    useCounterpartyLookup,
    useEntityLookup,
    useFinancialAccountLookup,
    useTaskLookup,
} from "./useEntityRefLookups";
import { useTaskRefLink, type EntityRefLink } from "./useTaskRefLink";
import styles from "./EntityRefPill.module.scss";
import glyphStyles from "./RefGlyph.module.scss";

export interface EntityRefPillProps {
    entityRef: EntityRef;
    isSelected?: boolean;
    attributes?: RenderElementProps["attributes"];
    children?: React.ReactNode;
    link?: EntityRefLink;
}

interface PillRef {
    kind: string;
    id: string | number;
    label: string;
}

interface PillShellProps extends Omit<
    EntityRefPillProps,
    "link" | "entityRef"
> {
    entityRef: PillRef;
    icon: React.ReactNode;
    tooltip?: string;
    isUnresolved?: boolean;
    link?: EntityRefLink | null;
}

const PillShell: React.FC<PillShellProps> = ({
    entityRef,
    icon,
    tooltip,
    isUnresolved = false,
    link,
    isSelected = false,
    attributes,
    children,
}) => {
    const isLink = !!link && !attributes;
    const className = classNames(styles.pill, {
        [styles.pillSelected]: isSelected,
        [styles.pillUnresolved]: isUnresolved,
        [styles.pillLink]: isLink,
    });
    const content = (
        <span contentEditable={false} className={styles.content}>
            <span className={styles.icon} aria-hidden>
                {icon}
            </span>
            <span className={styles.label}>{entityRef.label}</span>
            {children}
        </span>
    );

    return (
        <Tooltip content={tooltip}>
            {isLink ? (
                <StateAwareNavLink
                    to={link.to}
                    replace={link.replace}
                    state={link.state}
                    className={className}
                    data-entity-ref-kind={entityRef.kind}
                    data-entity-ref-id={entityRef.id}
                >
                    {content}
                </StateAwareNavLink>
            ) : (
                <span
                    {...attributes}
                    contentEditable={attributes ? false : undefined}
                    className={className}
                    data-entity-ref-kind={entityRef.kind}
                    data-entity-ref-id={entityRef.id}
                >
                    {content}
                </span>
            )}
        </Tooltip>
    );
};

type PillProps<TKind extends EntityRefKind> = Omit<
    EntityRefPillProps,
    "entityRef"
> & { entityRef: Extract<EntityRef, { kind: TKind }> };

const FinancialAccountPill: React.FC<PillProps<"financialAccount">> = (
    props,
) => {
    const lookup = useFinancialAccountLookup();
    const resolution = resolveEntityRef({
        lookup,
        key: props.entityRef.id,
    });

    return (
        <PillShell
            {...props}
            icon={
                resolution.state === "resolved" ? (
                    <AccountPickerIcon account={resolution.item} />
                ) : (
                    <InstitutionIcon size="xs" />
                )
            }
            tooltip={
                resolution.state === "resolved"
                    ? getAccountPickerDescription(resolution.item)
                    : getUnresolvedTooltip({
                          resolution,
                          copy: "Account no longer available",
                      })
            }
            isUnresolved={resolution.state === "unresolved"}
        />
    );
};

const EntityPill: React.FC<PillProps<"entity">> = (props) => {
    const lookup = useEntityLookup();
    const { entityRef } = props;
    const resolution = resolveEntityRef({ lookup, key: entityRef.id });
    const avatarEntity =
        resolution.state === "resolved"
            ? resolution.item
            : {
                  name: entityRef.label,
                  iconColor: "",
                  logo: null,
                  isPersonal: true,
              };

    return (
        <PillShell
            {...props}
            icon={<EntityAvatar entity={avatarEntity} size="xs" aria-hidden />}
            tooltip={getUnresolvedTooltip({
                resolution,
                copy: "Entity no longer available",
            })}
            isUnresolved={resolution.state === "unresolved"}
        />
    );
};

const CounterpartyPill: React.FC<PillProps<"counterparty">> = (props) => {
    const lookup = useCounterpartyLookup(props.entityRef.id);
    const resolution = resolveEntityRef({ lookup, key: props.entityRef.id });

    return (
        <PillShell
            {...props}
            icon={<CounterpartyGlyph />}
            tooltip={getUnresolvedTooltip({
                resolution,
                copy: "Counterparty no longer available",
            })}
            isUnresolved={resolution.state === "unresolved"}
        />
    );
};

const TaskPill: React.FC<PillProps<"task">> = (props) => {
    const lookup = useTaskLookup();
    const resolution = resolveEntityRef({ lookup, key: props.entityRef.id });
    const refLink = useTaskRefLink(props.entityRef.id);
    const link = props.link ?? refLink;

    return (
        <PillShell
            {...props}
            icon={<TaskGlyph />}
            tooltip={getUnresolvedTooltip({
                resolution,
                copy: "Task no longer available",
            })}
            isUnresolved={resolution.state === "unresolved"}
            link={resolution.state === "unresolved" ? null : link}
        />
    );
};

function getUnresolvedTooltip<TItem>({
    resolution,
    copy,
}: {
    resolution: EntityRefResolution<TItem>;
    copy: string;
}): string | undefined {
    return resolution.state === "unresolved" ? copy : undefined;
}

export type LinkPillKind = "transaction" | "journalEntry";

export interface LinkPillProps {
    kind: LinkPillKind;
    id: string;
    label: string;
    to: string;
}

const LINK_PILL_ICONS: Record<LinkPillKind, React.ReactNode> = {
    transaction: <TransactionIcon className={glyphStyles.glyph} aria-hidden />,
    journalEntry: <LedgerIcon className={glyphStyles.glyph} aria-hidden />,
};

export const LinkPill: React.FC<LinkPillProps> = ({ kind, id, label, to }) => (
    <PillShell
        entityRef={{ kind, id, label }}
        icon={LINK_PILL_ICONS[kind]}
        link={{ to }}
    />
);

const FALLBACK_ICON = <InstitutionIcon size="xs" />;

export const EntityRefPill: React.FC<EntityRefPillProps> = (props) => {
    const { entityRef } = props;
    switch (entityRef.kind) {
        case "financialAccount":
            return <FinancialAccountPill {...props} entityRef={entityRef} />;
        case "entity":
            return <EntityPill {...props} entityRef={entityRef} />;
        case "counterparty":
            return <CounterpartyPill {...props} entityRef={entityRef} />;
        case "task":
            return <TaskPill {...props} entityRef={entityRef} />;
        default:
            return <PillShell {...props} icon={FALLBACK_ICON} />;
    }
};
